import { useCallback, useState } from 'react';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api';
import { ibhApiService } from '@hub/bearing-hub/common/api-service';
import type {
  IbhCustomArticleFileModelParams,
  IbhProductionMethods,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhGeoFileType } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { UnitSystem } from '@igus/kopla-domain';

type UseFetchThreeDimensionalModelProps = {
  articleNumber: string;
  session: Session;
  isCustom: boolean;
  shapeKey: IbhShape;
  dimensions: IbhDimensions;
  unitSystem: UnitSystem;
  productionMethod: IbhProductionMethods;
};

export const useFetchThreeDimensionalModel = ({
  articleNumber,
  session,
  shapeKey,
  dimensions,
  isCustom,
  unitSystem,
  productionMethod,
}: UseFetchThreeDimensionalModelProps) => {
  const { sessionId } = session ?? {};
  const [modelLink, setModelLink] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  const fetchModelLink = useCallback(async () => {
    if (!articleNumber || !sessionId) {
      return;
    }

    try {
      const customParams = {
        articleNumber,
        fileType: IbhGeoFileType.Stl,
        shape: shapeKey,
        sid: sessionId,
        unitSystem,
        ...dimensions,
      } as IbhCustomArticleFileModelParams;

      const catalogParams = {
        articleNumber,
        fileType: IbhGeoFileType.Stl,
        shape: shapeKey,
        sid: sessionId,
        productionMethod,
      };

      const response = isCustom
        ? await ibhApiService.customArticleFileModel(customParams)
        : await ibhApiService.catalogArticleFileModel(catalogParams);

      const blob = await response.blob();
      setModelLink(URL.createObjectURL(blob));
    } catch (err) {
      setModelLink(null);
      setError(`Failed to fetch 3D model`);
    }
  }, [articleNumber, sessionId, shapeKey, dimensions, isCustom, unitSystem, productionMethod]);

  return { modelLink, error, fetchModelLink };
};
